























































































import { Component, Vue, Prop, Model, Watch } from 'vue-property-decorator'
import { Patient } from '@/types/user'
import BleConnectionManager from '@/ble/connectionManager'
import { BleProviderEventTypes } from '@/ble/constants'
import { BleDeviceTypes } from '@/constants/ble'
import { VitalsignPayload } from '@/api/vitalsign/types'
import dayjs from 'dayjs'

const DATE_FORMAT = 'YYYY-MM-DD HH:mm'
const YEAR_RANGE = 3

export type Props = {
  patient: Patient | undefined
  measureDatetime: string | undefined
}

@Component
export default class InviteWorkerModal extends Vue {
  @Model('change', { type: Boolean }) show!: boolean
  @Prop(Object) readonly props!: Props
  vitalsign: VitalsignPayload = {}
  unsubscribe!: () => void
  labels = [
    {
      label: 'SpO2',
      prop: 'spo2'
    },
    {
      label: '血圧（上）',
      prop: 'sbp'
    },
    {
      label: '血圧（下）',
      prop: 'dbp'
    },
    {
      label: '脈拍数',
      prop: 'pulseRate'
    },
    {
      label: '体温',
      prop: 'temperature'
    },
    {
      label: '血糖値',
      prop: 'glucose'
    },
    {
      label: '体重',
      prop: 'weight'
    }
  ]

  @Watch('props', { deep: true })
  onPropsChanged() {
    this.$set(this.vitalsign, 'measureDatetime', this.props.measureDatetime)
  }

  get patient() {
    return this.props.patient
  }

  get years() {
    return [...Array(YEAR_RANGE).keys()].map(i => dayjs().year() - i)
  }

  get months() {
    return [...Array(12).keys()]
  }

  get days() {
    const lastDay = dayjs()
      .year(this.selectedYear)
      .month(this.selectedMonth)
      .date(1)
      .add(1, 'month')
      .subtract(1, 'day')
      .date()
    return [...Array(lastDay).keys()].map(i => i + 1)
  }

  get hours() {
    return [...Array(24).keys()]
  }

  get minutes() {
    return [...Array(60).keys()]
  }

  get selectedYear() {
    return dayjs().year()
  }

  get selectedMonth() {
    const date = dayjs(this.vitalsign.measureDatetime)
    return date.month()
  }

  set selectedMonth(month) {
    const date = dayjs(this.vitalsign.measureDatetime).month(month)
    this.$set(this.vitalsign, 'measureDatetime', date.format(DATE_FORMAT))
  }

  get selectedDay() {
    const date = dayjs(this.vitalsign.measureDatetime)
    return date.date()
  }

  set selectedDay(day) {
    const date = dayjs(this.vitalsign.measureDatetime).date(day)
    this.$set(this.vitalsign, 'measureDatetime', date.format(DATE_FORMAT))
  }

  get selectedHour() {
    const date = dayjs(this.vitalsign.measureDatetime)
    return date.hour()
  }

  set selectedHour(hour) {
    const date = dayjs(this.vitalsign.measureDatetime).hour(hour)
    this.$set(this.vitalsign, 'measureDatetime', date.format(DATE_FORMAT))
  }

  get selectedMinute() {
    const date = dayjs(this.vitalsign.measureDatetime)
    return date.minute()
  }

  set selectedMinute(minute) {
    const date = dayjs(this.vitalsign.measureDatetime).minute(minute)
    this.$set(this.vitalsign, 'measureDatetime', date.format(DATE_FORMAT))
  }

  async created() {
    this.vitalsign = this.createPayload()
    const provider = BleConnectionManager.getProvider()
    this.unsubscribe = provider.addListener(
      BleProviderEventTypes.RECEIVE_VALUE,
      async readVal => {
        switch (readVal.deviceType) {
          case BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR:
          case BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR:
            this.$set(this.vitalsign, 'sbp', readVal.systolicPressure)
            this.$set(this.vitalsign, 'dbp', readVal.diastolicPressure)
            this.$set(this.vitalsign, 'pulseRate', readVal.pr)
            break
          case BleDeviceTypes.ELECTRONIC_THERMOMETER:
          case BleDeviceTypes.NONCONTACT_THERMOMETER:
            this.$set(this.vitalsign, 'temperature', readVal.temperature)
            break
          case BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT:
            this.$set(this.vitalsign, 'glucose', readVal.glucose)
            break
          case BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT:
          case BleDeviceTypes.SPO2_MONITOR_TM1121:
            this.$set(this.vitalsign, 'spo2', readVal.spo2)
            this.$set(this.vitalsign, 'pulseRate', readVal.pr)
            break
        }
      }
    )
  }

  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe()
  }

  close() {
    this.$store.commit('ble/clearReceivedValues')
    this.vitalsign = {
      measureDatetime: this.vitalsign.measureDatetime
    }
    this.$emit('close')
  }

  createPayload() {
    const receivedValues = this.$store.getters['ble/receivedValues']

    const data = {
      heartRate: receivedValues.heartRate,
      pulseRate: receivedValues.pulseRate,
      temperature: receivedValues.temperature,
      weight: receivedValues.weight,
      spo2: receivedValues.spo2,
      glucose: receivedValues.glucose,
      sbp: receivedValues.systolicPressure,
      dbp: receivedValues.diastolicPressure
    }

    return (Object.keys(data) as Array<keyof VitalsignPayload>).reduce(
      (payload, key) => {
        if ((data as VitalsignPayload)[key] === null) {
          return payload
        }

        return Object.assign(payload, {
          [key]: (data as VitalsignPayload)[key]
        })
      },
      {} as VitalsignPayload
    )
  }

  async handleClickSend() {
    if (!this.patient) return
    /* eslint-disable no-control-regex */
    const ALL_WHITESPACE_AND_CONTROL_REGEX = /[\s\p{C}]/gu
    /* eslint-enable no-control-regex */
    try {
      // cleanedVitalの生成（オブジェクトの各値をクリーンアップ）
      const cleanedVital = Object.fromEntries(
        Object.entries(this.vitalsign).map(([key, val]) => [
          key,
          typeof val === 'string'
            ? val.replace(ALL_WHITESPACE_AND_CONTROL_REGEX, '').trim() || null
            : val
        ])
      ) as Record<string, any>
      // 判定対象のキー（ラベルで使ってるもの＝バイタル項目のみ）
      const vitalKeys = this.labels.map(label => label.prop)
      // バイタルデータが全て空か判定
      const isAllEmpty = vitalKeys.every(
        key =>
          cleanedVital[key] === null ||
          cleanedVital[key] === undefined ||
          cleanedVital[key] === ''
      )
      if (isAllEmpty) {
        // 必要ならここでアラートや何もしないなどの処理
        this.close()
        return
      }
      // payloadの生成
      const payload = {
        ...cleanedVital,
        measureDatetime: dayjs().format(DATE_FORMAT)
      }

      // ↓ payload をAPIに渡す
      await Vue.$api.vitalsign.create({
        patientId: this.patient.id,
        payload
      })

      this.close()
    } catch (e) {
      console.log(e)
      const errors = await e.response.json()
      this.$store.dispatch('alert/showErrorResponse', errors)
    }
  }

  handleClickClose() {
    this.close()
  }

  handleSubmit() {
    this.close()
  }
}
